import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import {
  getDashboard,
  getFetchDashboardStatus,
  getModelDashboards,
  getSelectedDashboardId,
  getSelectedPage,
  initializeModelDashboards,
  listModelDashboards,
  loadDashboardById,
  setSelectedPage,
} from 'redux/DashboardSliceV2';
import { getBlocks } from 'redux/ModelsSlice';
import { STATUS } from 'projectConstants';
import { buildModelDashboardUrl, parseModelDashboardPath } from 'utils/dashboardModelRoutes';

const sortPages = (pages: any[] | undefined) =>
  [...(pages || [])].sort((a, b) => (a?.position ?? 0) - (b?.position ?? 0));

const readStoredPageId = (modelId: string | number): string | null => {
  try {
    const raw = localStorage.getItem('global-modal-filters');
    if (!raw) return null;
    const data = JSON.parse(raw) as Record<string, { selectedPageId?: string | number }>;
    const entry = data[Number(modelId)];
    if (entry?.selectedPageId == null) return null;
    return String(entry.selectedPageId);
  } catch {
    return null;
  }
};

const pickDefaultPage = (pages: any[] | undefined, modelId: string | number) => {
  const sorted = sortPages(pages);
  if (sorted.length === 0) return null;
  const storedId = readStoredPageId(modelId);
  if (storedId) {
    const match = sorted.find((p) => String(p?.id) === storedId);
    if (match) return match;
  }
  return sorted[0];
};

/**
 * Single entry point for model dashboard list load, URL sync, and pack bootstrap.
 */
const useModelDashboardBootstrap = (enabled: boolean) => {
  const dispatch: any = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { modelId: routeModelId } = useParams();
  const model = useSelector(getBlocks);
  const dashboard = useSelector(getDashboard);
  const modelDashboards = useSelector(getModelDashboards);
  const selectedDashboardId = useSelector(getSelectedDashboardId);
  const selectedPage = useSelector(getSelectedPage);
  const fetchDashboardStatus = useSelector(getFetchDashboardStatus);
  const listStatus = useSelector(
    (state: any) => state.dashboardSliceV2?.listModelDashboardsStatus ?? STATUS.IDLE,
  );

  const listedForModelIdReference = useRef<number | null>(null);
  const indexBootstrapKeyReference = useRef<string | null>(null);
  const loadInFlightReference = useRef<string | null>(null);

  const parsed = parseModelDashboardPath(location.pathname);
  const modelId = parsed?.modelId ?? routeModelId;
  const routeDashboardId = parsed?.mode === 'dashboard-detail' ? parsed.dashboardId ?? null : null;
  const isDashboardsIndex = parsed?.mode === 'dashboards-index';

  useEffect(() => {
    listedForModelIdReference.current = null;
    indexBootstrapKeyReference.current = null;
    loadInFlightReference.current = null;
  }, [model?.id]);

  useEffect(() => {
    if (!enabled || !model?.id) {
      return;
    }
    const numericModelId = Number(model.id);
    if (listStatus === STATUS.LOADING) {
      return;
    }
    if (listStatus === STATUS.SUCCESS && listedForModelIdReference.current === numericModelId) {
      return;
    }

    dispatch(listModelDashboards({ modelId: model.id })).then((result: any) => {
      if (result.meta?.requestStatus === 'fulfilled') {
        listedForModelIdReference.current = numericModelId;
      }
    });
  }, [dispatch, enabled, listStatus, model?.id]);

  useEffect(() => {
    if (!enabled || !isDashboardsIndex || !modelId) {
      return;
    }
    const bootstrapKey = `${modelId}:dashboards-index`;
    if (indexBootstrapKeyReference.current === bootstrapKey) {
      return;
    }

    const redirectToPack = (dashboardId: string) => {
      indexBootstrapKeyReference.current = bootstrapKey;
      navigate(buildModelDashboardUrl(modelId, dashboardId), { replace: true });
    };

    const existingId =
      selectedDashboardId ??
      (dashboard?.id ? String(dashboard.id) : null) ??
      modelDashboards[0]?.id ??
      null;

    if (existingId && dashboard?.id && String(dashboard.id) === existingId) {
      redirectToPack(existingId);
      return;
    }

    if (!model?.id || String(model.id) !== String(modelId)) {
      return;
    }

    indexBootstrapKeyReference.current = bootstrapKey;
    dispatch(initializeModelDashboards({ model })).then((result: any) => {
      if (result.meta?.requestStatus !== 'fulfilled') {
        indexBootstrapKeyReference.current = null;
        return;
      }
      const payload = result.payload as { selectedDashboardId: string };
      redirectToPack(payload.selectedDashboardId);
    });
  }, [
    dashboard?.id,
    dispatch,
    enabled,
    isDashboardsIndex,
    model?.id,
    modelDashboards,
    modelId,
    navigate,
    selectedDashboardId,
  ]);

  useEffect(() => {
    if (!enabled || !routeDashboardId || !modelId) {
      return;
    }
    if (
      dashboard?.id &&
      String(dashboard.id) === routeDashboardId &&
      fetchDashboardStatus === STATUS.SUCCESS
    ) {
      return;
    }
    if (loadInFlightReference.current === routeDashboardId) {
      return;
    }
    loadInFlightReference.current = routeDashboardId;
    dispatch(loadDashboardById({ dashboardId: routeDashboardId, modelId })).finally(() => {
      if (loadInFlightReference.current === routeDashboardId) {
        loadInFlightReference.current = null;
      }
    });
  }, [dashboard?.id, dispatch, enabled, fetchDashboardStatus, modelId, routeDashboardId]);

  useEffect(() => {
    if (!enabled || !routeDashboardId || !dashboard?.pages) {
      return;
    }
    const searchParameters = new URLSearchParams(location.search);
    const pageId = searchParameters.get('pageId');
    const sorted = sortPages(dashboard.pages);

    if (pageId) {
      const matched = sorted.find((p) => String(p?.id) === pageId) ?? null;
      if (matched && String(selectedPage?.id) !== String(matched.id)) {
        dispatch(setSelectedPage(matched));
      }
      return;
    }

    const defaultPage = pickDefaultPage(dashboard.pages, modelId!);
    if (defaultPage && String(selectedPage?.id) !== String(defaultPage.id)) {
      dispatch(setSelectedPage(defaultPage));
    }
  }, [
    dashboard?.pages,
    dispatch,
    enabled,
    location.search,
    modelId,
    routeDashboardId,
    selectedPage?.id,
  ]);
};

export default useModelDashboardBootstrap;
